/**
 * Decides what a screen should be showing at a given instant.
 *
 * This is a pure function of (manifest, clock) and nothing else — no I/O, no
 * ambient time, no device timezone. That is what lets the admin panel's preview
 * and the Kotlin player agree exactly, and what lets both be tested against the
 * same fixtures in `docs/manifest-contract.md`.
 *
 * Resolution order, first one with something playable wins:
 *
 *   1. Closed        outside the store's opening hours
 *   2. Override      an active "play this now", most specific scope already picked
 *   3. Schedule      the highest-priority daypart rule matching right now
 *   4. Default       the screen's everyday playlist
 *   5. Fallback      the screen's single standby asset
 *   6. Idle          nothing playable; the app shows its built-in card
 *
 * An override that cannot be shown yet — its video is still downloading — falls
 * through to the schedule rather than blanking the screen.
 */

import {
  DEFAULT_BACKGROUND_COLOR,
  DEFAULT_FIT,
  DEFAULT_IMAGE_DURATION_SEC,
  DEFAULT_VIDEO_REPEAT_COUNT,
} from './constants.js';
import type {
  DeviceManifest,
  FitMode,
  ManifestAsset,
  ManifestOverride,
  PlaylistItem,
  ScheduleRule,
} from './manifest.js';
import { isVideoAsset } from './manifest.js';
import type { ZonedParts } from './time.js';
import { isWithinWeeklyWindow, parseHhMm, zonedParts } from './time.js';

export type PlaybackSource = 'override' | 'schedule' | 'default' | 'fallback';

interface ResolvedItemBase {
  assetId: string;
  asset: ManifestAsset;
  fit: FitMode;
  backgroundColor: string;
}

export interface ResolvedImageItem extends ResolvedItemBase {
  kind: 'image';
  durationSec: number;
}

export interface ResolvedVideoItem extends ResolvedItemBase {
  kind: 'video';
  repeatCount: number;
  muted: boolean;
}

export type ResolvedItem = ResolvedImageItem | ResolvedVideoItem;

export type ResolvedState =
  | { kind: 'closed' }
  | { kind: 'idle'; reason: 'no-content' | 'nothing-playable' }
  | {
      kind: 'playing';
      source: PlaybackSource;
      playlistId: string | null;
      items: ResolvedItem[];
    };

export interface ResolveOptions {
  /**
   * On a device, only assets that are fully downloaded and hash-verified count.
   * Omitted in the admin preview, where everything streams from the CDN.
   */
  isAssetAvailable?: (assetId: string) => boolean;
}

export function resolve(
  manifest: DeviceManifest,
  nowMs: number,
  options: ResolveOptions = {},
): ResolvedState {
  const now = zonedParts(nowMs, manifest.timezone);

  if (!isOpenAt(manifest, now)) return { kind: 'closed' };

  let sawConfiguredContent = false;

  for (const candidate of candidates(manifest, nowMs, now)) {
    if (candidate.items.length > 0) sawConfiguredContent = true;
    const items = materialise(manifest, candidate.items, nowMs, options);
    if (items.length > 0) {
      return {
        kind: 'playing',
        source: candidate.source,
        playlistId: candidate.playlistId,
        items,
      };
    }
  }

  if (manifest.fallbackAssetId !== null) {
    sawConfiguredContent = true;
    const items = materialise(
      manifest,
      [defaultItemFor(manifest.fallbackAssetId)],
      nowMs,
      options,
    );
    if (items.length > 0) {
      return { kind: 'playing', source: 'fallback', playlistId: null, items };
    }
  }

  return {
    kind: 'idle',
    reason: sawConfiguredContent ? 'nothing-playable' : 'no-content',
  };
}

/** True when the store is open. No opening hours configured means always open. */
export function isOpenAt(
  manifest: Pick<DeviceManifest, 'openingHours'>,
  now: Pick<ZonedParts, 'isoDayOfWeek' | 'minuteOfDay'>,
): boolean {
  if (manifest.openingHours.length === 0) return true;
  return manifest.openingHours.some((entry) =>
    isWithinWeeklyWindow(now, [entry.day], parseHhMm(entry.open), parseHhMm(entry.close)),
  );
}

/** The active override, or null if there is none or it has not started / has ended. */
export function activeOverride(
  manifest: Pick<DeviceManifest, 'override'>,
  nowMs: number,
): ManifestOverride | null {
  const override = manifest.override;
  if (!override) return null;
  if (override.startsAt !== null && Date.parse(override.startsAt) > nowMs) return null;
  if (Date.parse(override.endsAt) <= nowMs) return null;
  return override;
}

/** The highest-priority schedule rule matching now; ties favour the earlier rule. */
export function matchingRule(
  manifest: Pick<DeviceManifest, 'schedule'>,
  now: Pick<ZonedParts, 'isoDayOfWeek' | 'minuteOfDay'>,
): ScheduleRule | null {
  let best: ScheduleRule | null = null;
  for (const rule of manifest.schedule) {
    if (!isWithinWeeklyWindow(now, rule.days, parseHhMm(rule.start), parseHhMm(rule.end))) {
      continue;
    }
    if (best === null || rule.priority > best.priority) best = rule;
  }
  return best;
}

/**
 * Every asset the screen needs on disk, across all dayparts — not just the one
 * playing now. This is what the download manager works from, and what the LRU
 * treats as pinned.
 */
export function requiredAssetIds(manifest: DeviceManifest): string[] {
  const ids = new Set<string>();

  const addPlaylist = (playlistId: string | null): void => {
    if (playlistId === null) return;
    for (const item of manifest.playlists[playlistId]?.items ?? []) {
      ids.add(item.assetId);
    }
  };

  for (const rule of manifest.schedule) addPlaylist(rule.playlistId);
  addPlaylist(manifest.defaultPlaylistId);

  if (manifest.override) {
    addPlaylist(manifest.override.playlistId);
    if (manifest.override.assetId !== null) ids.add(manifest.override.assetId);
  }

  if (manifest.fallbackAssetId !== null) ids.add(manifest.fallbackAssetId);

  return [...ids].filter((id) => manifest.assets[id] !== undefined);
}

/** Bytes the screen must hold to have everything in its manifest. */
export function requiredBytes(manifest: DeviceManifest): number {
  return requiredAssetIds(manifest).reduce(
    (total, id) => total + (manifest.assets[id]?.bytes ?? 0),
    0,
  );
}

interface Candidate {
  source: PlaybackSource;
  playlistId: string | null;
  items: PlaylistItem[];
}

function* candidates(
  manifest: DeviceManifest,
  nowMs: number,
  now: ZonedParts,
): Generator<Candidate> {
  const override = activeOverride(manifest, nowMs);
  if (override) {
    if (override.assetId !== null) {
      yield {
        source: 'override',
        playlistId: null,
        items: [defaultItemFor(override.assetId)],
      };
    } else if (override.playlistId !== null) {
      yield {
        source: 'override',
        playlistId: override.playlistId,
        items: itemsOf(manifest, override.playlistId),
      };
    }
  }

  const rule = matchingRule(manifest, now);
  if (rule) {
    yield {
      source: 'schedule',
      playlistId: rule.playlistId,
      items: itemsOf(manifest, rule.playlistId),
    };
  }

  if (manifest.defaultPlaylistId !== null) {
    yield {
      source: 'default',
      playlistId: manifest.defaultPlaylistId,
      items: itemsOf(manifest, manifest.defaultPlaylistId),
    };
  }
}

function itemsOf(manifest: DeviceManifest, playlistId: string): PlaylistItem[] {
  return manifest.playlists[playlistId]?.items ?? [];
}

function defaultItemFor(assetId: string): PlaylistItem {
  return {
    assetId,
    durationSec: null,
    repeatCount: null,
    muted: true,
    fit: DEFAULT_FIT,
    backgroundColor: DEFAULT_BACKGROUND_COLOR,
  };
}

function materialise(
  manifest: DeviceManifest,
  items: readonly PlaylistItem[],
  nowMs: number,
  options: ResolveOptions,
): ResolvedItem[] {
  const resolved: ResolvedItem[] = [];

  for (const item of items) {
    const asset = manifest.assets[item.assetId];
    if (!asset) continue;
    if (asset.contentExpiresAt !== null && Date.parse(asset.contentExpiresAt) <= nowMs) continue;
    if (options.isAssetAvailable && !options.isAssetAvailable(item.assetId)) continue;
    resolved.push(toResolvedItem(item, asset));
  }

  return resolved;
}

function toResolvedItem(item: PlaylistItem, asset: ManifestAsset): ResolvedItem {
  const base: ResolvedItemBase = {
    assetId: item.assetId,
    asset,
    fit: item.fit,
    backgroundColor: item.backgroundColor,
  };

  if (isVideoAsset(asset)) {
    return {
      ...base,
      kind: 'video',
      repeatCount: item.repeatCount ?? DEFAULT_VIDEO_REPEAT_COUNT,
      muted: item.muted,
    };
  }

  return {
    ...base,
    kind: 'image',
    durationSec: item.durationSec ?? DEFAULT_IMAGE_DURATION_SEC,
  };
}
